import { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Home } from './components/Home';
import { AboutUs } from './components/AboutUs';
import { Product } from './components/Product';
import { ProductDetail } from './components/ProductDetail';
import { News } from './components/News';
import { Knowledge } from './components/Knowledge';
import { ManualDownload } from './components/ManualDownload';
import { ContactUs } from './components/ContactUs';
import { SEO } from '../components/SEO';

interface NavigationParams {
  category?: string;
  brand?: string;
  productId?: number;
}

// Page to URL mapping
const pageToPath: Record<string, string> = {
  'Home': '',
  'About Us': 'about',
  'Product': 'products',
  'ProductDetail': 'product',
  'News': 'news',
  'Knowledge': 'knowledge',
  'Manual download': 'manuals',
  'Contact Us': 'contact'
};

// URL to Page mapping
const pathToPage: Record<string, string> = {
  '': 'Home',
  'about': 'About Us',
  'products': 'Product',
  'product': 'ProductDetail',
  'news': 'News',
  'knowledge': 'Knowledge',
  'manuals': 'Manual download',
  'contact': 'Contact Us'
};

// Parse hash to get page and params
const parseHash = (): { page: string; params: NavigationParams } => {
  const hash = window.location.hash.slice(1); // Remove #
  const [path, queryString] = hash.split('?');
  const page = pathToPage[path] || 'Home';
  const params: NavigationParams = {};
  
  if (queryString) {
    const searchParams = new URLSearchParams(queryString);
    if (searchParams.get('category')) params.category = searchParams.get('category')!;
    if (searchParams.get('brand')) params.brand = searchParams.get('brand')!;
    if (searchParams.get('id')) params.productId = parseInt(searchParams.get('id')!);
  }
  
  return { page, params };
};

// Build hash from page and params
const buildHash = (page: string, params?: NavigationParams): string => {
  const path = pageToPath[page] || '';
  const queryParts: string[] = [];
  
  if (params?.category) queryParts.push(`category=${params.category}`);
  if (params?.brand) queryParts.push(`brand=${params.brand}`);
  if (params?.productId) queryParts.push(`id=${params.productId}`);
  
  const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  return `#${path}${query}`;
};

export default function App() {
  const [currentPage, setCurrentPage] = useState('Home');
  const [navigationParams, setNavigationParams] = useState<NavigationParams>({});

  // Initialize from URL hash on mount
  useEffect(() => {
    const { page, params } = parseHash();
    setCurrentPage(page);
    setNavigationParams(params);

    // Listen for hash changes (browser back/forward)
    const handleHashChange = () => {
      const { page, params } = parseHash();
      setCurrentPage(page);
      setNavigationParams(params);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleNavigate = (page: string, params?: NavigationParams) => {
    setCurrentPage(page);
    setNavigationParams(params || {});
    // Update URL hash
    window.location.hash = buildHash(page, params);
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getSEOData = () => {
    const baseUrl = 'https://dchparts.com';
    const baseStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD.',
      alternateName: 'DCH',
      url: baseUrl,
      logo: `${baseUrl}/logo.png`,
      description: 'Professional engineering parts supplier, providing DAOCHIN work platform parts, forklift parts, hydraulic system parts and other industrial components',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'NO.1990-1, Jinbi Road, Fengxian District',
        addressLocality: 'Shanghai',
        addressRegion: 'Shanghai',
        addressCountry: 'CN'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+864008821928',
        contactType: 'customer service',
        email: 'dch-equips@outlook.com',
        availableLanguage: ['zh-CN', 'en']
      },
      legalName: 'SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD.',
      identifier: {
        '@type': 'PropertyValue',
        name: 'ICP License',
        value: '沪ICP备2026000796号'
      }
    };

    switch (currentPage) {
      case 'Home':
        return {
          title: 'Professional Engineering Parts Supplier - DAOCHIN (DCH) | DAOCHIN Platform Parts, Forklift Parts',
          description: 'SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) - Providing DAOCHIN work platform parts, forklift parts, hydraulic system parts and other industrial components. 20+ years experience, 5000+ products, serving 50+ countries worldwide',
          keywords: 'DAOCHIN,DCH,engineering parts,DAOCHIN platform parts,forklift parts,hydraulic parts,industrial components,Genie parts,JLG parts,Skyjack parts',
          url: baseUrl,
          structuredData: {
            ...baseStructuredData,
            '@type': 'WebSite',
            potentialAction: {
              '@type': 'SearchAction',
              target: `${baseUrl}/products?q={search_term_string}`,
              'query-input': 'required name=search_term_string'
            }
          }
        };
      case 'About Us':
        return {
          title: 'About Us - DAOCHIN (DCH) | 20+ Years Professional Engineering Parts Supplier',
          description: 'SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) established in 2004, is a leading industrial parts supplier with 20+ years experience, serving 50+ countries with high-quality engineering parts',
          keywords: 'about us,company profile,DAOCHIN,DCH,engineering parts supplier,industrial components,company history',
          url: `${baseUrl}/about`,
          structuredData: {
            ...baseStructuredData,
            '@type': 'AboutPage',
            mainEntity: {
              '@type': 'Organization',
              foundingDate: '2004',
              numberOfEmployees: '100-500',
              areaServed: 'Worldwide'
            }
          }
        };
      case 'Product':
        return {
          title: 'Products - DAOCHIN (DCH) | Engineering Parts Catalog',
          description: 'Browse SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) engineering parts catalog, including DAOCHIN platform parts, forklift parts, hydraulic system parts and 5000+ products',
          keywords: 'product catalog,engineering parts,DAOCHIN,DCH,DAOCHIN platform parts,forklift parts,hydraulic parts',
          url: `${baseUrl}/products`,
          structuredData: {
            ...baseStructuredData,
            '@type': 'CollectionPage',
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: []
            }
          }
        };
      case 'ProductDetail':
        return {
          title: 'Product Details - DAOCHIN (DCH)',
          description: 'View SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) product specifications, technical parameters and pricing information',
          keywords: 'product details,specifications,technical parameters,DAOCHIN,DCH',
          url: `${baseUrl}/products/${navigationParams.productId}`,
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'Engineering Parts Product',
            description: 'High-quality engineering parts product',
            brand: {
              '@type': 'Brand',
              name: 'SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD.',
              alternateName: 'DCH'
            },
            offers: {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              priceCurrency: 'USD'
            }
          }
        };
      case 'News':
        return {
          title: 'News - DAOCHIN (DCH) | Latest Industry Updates',
          description: 'Stay updated with SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) latest product releases, industry news and company updates',
          keywords: 'news,industry updates,company news,product releases,DAOCHIN,DCH',
          url: `${baseUrl}/news`,
          structuredData: {
            ...baseStructuredData,
            '@type': 'Blog'
          }
        };
      case 'Knowledge':
        return {
          title: 'Knowledge Base - DAOCHIN (DCH) | Technical Documents and Guides',
          description: 'Access SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) technical resources, guides and best practices to help you make informed decisions',
          keywords: 'knowledge base,technical documents,installation guides,maintenance manuals,DAOCHIN,DCH',
          url: `${baseUrl}/knowledge`,
          structuredData: {
            ...baseStructuredData,
            '@type': 'FAQPage'
          }
        };
      case 'Manual download':
        return {
          title: 'Manual Download - DAOCHIN (DCH) | Product Manuals and Technical Documents',
          description: 'Download SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) installation guides, technical manuals and product documentation',
          keywords: 'manual download,technical documents,installation guides,product manuals,DAOCHIN,DCH',
          url: `${baseUrl}/manuals`,
          structuredData: {
            ...baseStructuredData,
            '@type': 'CollectionPage'
          }
        };
      case 'Contact Us':
        return {
          title: 'Contact Us - DAOCHIN (DCH) | Customer Service',
          description: 'Contact SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) team for product inquiries, technical support or business cooperation',
          keywords: 'contact us,customer service,technical support,business inquiry,DAOCHIN,DCH',
          url: `${baseUrl}/contact`,
          structuredData: {
            ...baseStructuredData,
            '@type': 'ContactPage'
          }
        };
      default:
        return {
          title: 'Professional Engineering Parts Supplier - DAOCHIN (DCH)',
          description: 'SHANGHAI DAOCHIN MECHANICAL EQUIPMENT CO., LTD. (DCH) - Providing DAOCHIN work platform parts, forklift parts, hydraulic system parts and other industrial components',
          keywords: 'DAOCHIN,DCH,engineering parts,industrial components',
          url: baseUrl,
          structuredData: baseStructuredData
        };
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'Home':
        return <Home onNavigate={handleNavigate} />;
      case 'About Us':
        return <AboutUs />;
      case 'Product':
        return (
          <Product 
            initialCategory={navigationParams.category} 
            initialBrand={navigationParams.brand}
            onNavigate={handleNavigate} 
          />
        );
      case 'ProductDetail':
        return navigationParams.productId ? (
          <ProductDetail productId={navigationParams.productId} onNavigate={handleNavigate} />
        ) : (
          <Product 
            initialCategory={navigationParams.category} 
            initialBrand={navigationParams.brand}
            onNavigate={handleNavigate} 
          />
        );
      case 'News':
        return <News />;
      case 'Knowledge':
        return <Knowledge />;
      case 'Manual download':
        return <ManualDownload />;
      case 'Contact Us':
        return <ContactUs />;
      default:
        return <Home onNavigate={handleNavigate} />;
    }
  };

  const seoData = getSEOData();

  return (
    <>
      <SEO {...seoData} />
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header currentPage={currentPage} onNavigate={handleNavigate} />
      <main className="flex-1">
        {renderPage()}
      </main>
      <Footer onNavigate={handleNavigate} />
    </div>
    </>
  );
}